import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  FaChevronRight,
  FaArrowLeft,
  FaArrowRight,
  FaExternalLinkAlt,
  FaBars,
  FaTimes,
} from "react-icons/fa";
import {
  itemsByCategory,
  getTourPosition,
  OPEN_SOURCE_TOUR,
} from "../../data/openSourceItems";

const TOUR_LENGTH = OPEN_SOURCE_TOUR.length;

/**
 * Consistent chrome for every Open Source tool page: a breadcrumb, a category
 * sidebar with the current tool highlighted, and a Prev/Next bar that steps
 * through the whole collection in order.
 *
 * It wraps each tool at the ROUTE level (see App.js) so the tools' own working
 * internals are never touched — the calculator renders as {children} in the
 * content column. The two light-themed tools keep their own look inside this
 * dark frame, which reads as a light document panel rather than a clash.
 */
const ToolLayout = ({ id, children }) => {
  const groups = itemsByCategory();
  const { item, prev, next, index } = getTourPosition(id);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const activeCategory =
    item ? groups.find((g) => g.items.some((i) => i.id === id)) : null;

  const Sidebar = ({ onNavigate }) => (
    <nav aria-label="Open Source tools">
      {groups.map((group) => (
        <div key={group.id} className="mb-6">
          <p className="mb-2 flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.16em] text-silver-dark">
            <group.icon className="text-xs" aria-hidden="true" />
            {group.label}
          </p>
          <ul className="space-y-0.5">
            {group.items.map((entry) => {
              const isActive = entry.id === id;
              const inner = (
                <span className="flex items-center justify-between gap-2">
                  <span className="truncate">{entry.title}</span>
                  {entry.href && (
                    <FaExternalLinkAlt className="text-[10px] opacity-60" aria-hidden="true" />
                  )}
                </span>
              );
              const base =
                "block rounded-md px-3 py-2 text-sm transition-colors duration-200";
              if (entry.href) {
                return (
                  <li key={entry.id}>
                    <a
                      href={entry.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={onNavigate}
                      className={`${base} text-silver-medium hover:bg-white/5 hover:text-silver-light`}
                    >
                      {inner}
                    </a>
                  </li>
                );
              }
              return (
                <li key={entry.id}>
                  <Link
                    to={entry.to}
                    onClick={onNavigate}
                    aria-current={isActive ? "page" : undefined}
                    className={`${base} ${isActive
                      ? "bg-moss/15 font-medium text-moss-light border-l-2 border-moss -ml-0.5 pl-[10px]"
                      : "text-silver-medium hover:bg-white/5 hover:text-silver-light"
                      }`}
                  >
                    {inner}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-black">
      {/* Breadcrumb + mobile sidebar toggle. No extra top padding — <main> in
          App.js already carries pt-16 to clear the fixed navbar. */}
      <div className="border-b border-gray-800 bg-black/90 backdrop-blur-sm">
        <div className="container mx-auto flex items-center justify-between gap-4 px-4 py-3">
          <nav aria-label="Breadcrumb" className="min-w-0">
            <ol className="flex items-center gap-2 text-sm text-silver-dark">
              <li>
                <Link to="/" className="hover:text-silver-light transition-colors">Home</Link>
              </li>
              <li aria-hidden="true"><FaChevronRight className="text-[10px]" /></li>
              <li>
                <Link to="/open-source" className="hover:text-silver-light transition-colors">
                  Open Source
                </Link>
              </li>
              {activeCategory && (
                <>
                  <li aria-hidden="true"><FaChevronRight className="text-[10px]" /></li>
                  <li>
                    <Link
                      to={`/open-source?tab=${activeCategory.id}`}
                      className="hover:text-silver-light transition-colors"
                    >
                      {activeCategory.label}
                    </Link>
                  </li>
                </>
              )}
              {item && (
                <>
                  <li aria-hidden="true"><FaChevronRight className="text-[10px]" /></li>
                  <li className="truncate font-medium text-silver-light">{item.title}</li>
                </>
              )}
            </ol>
          </nav>

          <button
            type="button"
            onClick={() => setMobileNavOpen((v) => !v)}
            className="flex items-center gap-2 rounded-md border border-gray-700 px-3 py-1.5 text-xs text-silver-medium lg:hidden"
            aria-expanded={mobileNavOpen}
          >
            {mobileNavOpen ? <FaTimes /> : <FaBars />}
            All tools
          </button>
        </div>

        {mobileNavOpen && (
          <div className="border-t border-gray-800 px-4 py-4 lg:hidden">
            <Sidebar onNavigate={() => setMobileNavOpen(false)} />
          </div>
        )}
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="flex gap-8">
          {/* Sticky sidebar — desktop only; mobile uses the toggle above */}
          <aside className="hidden w-60 flex-shrink-0 lg:block">
            <div className="sticky top-24">
              <Sidebar />
            </div>
          </aside>

          {/* Tool content + Prev/Next */}
          <div className="min-w-0 flex-1">
            {children}

            {item && (
              <nav
                aria-label="Between tools"
                className="mt-12 grid grid-cols-1 gap-4 border-t border-gray-800 pt-8 sm:grid-cols-2"
              >
                {prev ? (
                  <Link
                    to={prev.to}
                    className="group flex items-center gap-3 rounded-lg border border-gray-800 p-4 transition-colors hover:border-moss/50"
                  >
                    <FaArrowLeft className="flex-shrink-0 text-silver-dark transition-colors group-hover:text-moss-light" />
                    <span className="min-w-0">
                      <span className="block text-xs uppercase tracking-wider text-silver-dark">Previous</span>
                      <span className="block truncate font-medium text-silver-light">{prev.title}</span>
                    </span>
                  </Link>
                ) : (
                  <Link
                    to="/open-source"
                    className="group flex items-center gap-3 rounded-lg border border-gray-800 p-4 transition-colors hover:border-moss/50"
                  >
                    <FaArrowLeft className="flex-shrink-0 text-silver-dark transition-colors group-hover:text-moss-light" />
                    <span className="min-w-0">
                      <span className="block text-xs uppercase tracking-wider text-silver-dark">Back to</span>
                      <span className="block truncate font-medium text-silver-light">All Open Source</span>
                    </span>
                  </Link>
                )}

                {next ? (
                  <Link
                    to={next.to}
                    className="group flex items-center justify-end gap-3 rounded-lg border border-gray-800 p-4 text-right transition-colors hover:border-moss/50"
                  >
                    <span className="min-w-0">
                      <span className="block text-xs uppercase tracking-wider text-silver-dark">Next</span>
                      <span className="block truncate font-medium text-silver-light">{next.title}</span>
                    </span>
                    <FaArrowRight className="flex-shrink-0 text-silver-dark transition-colors group-hover:text-moss-light" />
                  </Link>
                ) : (
                  <Link
                    to="/open-source"
                    className="group flex items-center justify-end gap-3 rounded-lg border border-gray-800 p-4 text-right transition-colors hover:border-moss/50"
                  >
                    <span className="min-w-0">
                      <span className="block text-xs uppercase tracking-wider text-silver-dark">Finished the tour</span>
                      <span className="block truncate font-medium text-silver-light">Back to Open Source</span>
                    </span>
                    <FaArrowRight className="flex-shrink-0 text-silver-dark transition-colors group-hover:text-moss-light" />
                  </Link>
                )}
              </nav>
            )}

            {item && index >= 0 && (
              <p className="mt-4 text-center text-xs text-silver-dark">
                {`${index + 1} / ${TOUR_LENGTH}`}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ToolLayout;
